"use client"

import { Search, ArrowRight } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useState } from "react"

import { Badge } from "@/core/ui/badge"
import { Input } from "@/core/ui/input"
import { Newsletter } from "@/modules/marketing/writing/components/newsletter"

interface BlogPost {
  _id: string
  title: string
  slug: string
  excerpt: string
  date: string
  readTime: string
  category: string
  featured?: boolean
  coverImage?: string
}

interface BlogClientProps {
  initialPosts: BlogPost[]
}

export function BlogClient({ initialPosts }: BlogClientProps) {
  const categories = ["All", ...Array.from(new Set(initialPosts.map(post => post.category).filter(Boolean)))]

  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All")

  const filteredPosts = initialPosts.filter(post => {
    const matchesSearch = 
      post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  // Optional: Highlight the most recent post as a "Hero" article
  // Or honor the explicit 'featured' flag
  const specifiedHero = filteredPosts.find(p => p.featured)
  const heroPost = specifiedHero || (filteredPosts.length > 0 && selectedCategory === "All" && !searchQuery ? filteredPosts[0] : null)
  const gridPosts = heroPost ? filteredPosts.filter(p => p._id !== heroPost._id) : filteredPosts

  return (
    <section className="min-h-screen bg-black text-slate-200 selection:bg-blue-500/30 selection:text-white">

      {/* Professional Header */}
      <section className="pt-32 pb-20 container px-4 mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Insights & <span className="text-slate-500">Perspectives.</span>
          </h1>
          <p className="text-xl text-slate-400 leading-relaxed max-w-2xl">
            Expert analysis on enterprise technology, digital transformation, and scalable software architecture.
          </p>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="border-y border-white/5 bg-white/2">
        <div className="container px-4 mx-auto max-w-7xl h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 overflow-x-auto no-scrollbar h-full">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${selectedCategory === category
                  ? "text-white"
                  : "text-slate-500 hover:text-slate-300"
                  }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="hidden md:flex relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
            <Input
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 bg-transparent border-none text-white placeholder:text-slate-600 focus:ring-0"
            />
          </div>
        </div>
      </section>

      <section className="container px-4 mx-auto max-w-7xl py-16">

        {/* Hero Post (First item, featured) */}
        {heroPost && (
          <div className="mb-16">
            <Link href={`/blog/${heroPost.slug}`} className="group grid md:grid-cols-2 gap-8 items-center rounded-2xl p-4 md:p-8 hover:bg-white/5 transition-all duration-300 border border-transparent hover:border-white/5">
              <div className="relative aspect-16/10 w-full overflow-hidden rounded-xl bg-neutral-900">
                {/* Placeholder or Image */}
                <div className="absolute inset-0 bg-neutral-800" />
                {heroPost.coverImage && (
                  <Image
                    src={heroPost.coverImage}
                    alt={heroPost.title}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
              </div>
              <div className="md:pr-12">
                <div className="flex items-center gap-3 mb-4">
                  {heroPost.category && (
                    <Badge variant="outline" className="rounded-md border-premium-violet/30 text-premium-violet bg-premium-violet/5 hover:bg-premium-violet/10">
                      {heroPost.category}
                    </Badge>
                  )}
                  {heroPost.date && <span className="text-sm text-slate-500">{new Date(heroPost.date).toLocaleDateString()}</span>}
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight group-hover:text-premium-cyan transition-colors">
                  {heroPost.title}
                </h2>
                <p className="text-slate-400 text-lg mb-6 line-clamp-3">
                  {heroPost.excerpt}
                </p>
                <div className="flex items-center text-sm font-medium text-white group-hover:gap-2 transition-all">
                  Read Article <ArrowRight className="ml-2 w-4 h-4" />
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Standard Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {gridPosts.map(post => (
            <Link key={post._id} href={`/blog/${post.slug}`} className="group flex flex-col h-full rounded-2xl p-4 hover:bg-white/5 transition-all duration-300 border border-transparent hover:border-white/5">
              <div className="relative aspect-16/10 w-full overflow-hidden rounded-xl bg-neutral-900 mb-6">
                {/* Placeholder or Image */}
                <div className="absolute inset-0 bg-neutral-800" />
                {post.coverImage && (
                  <Image
                    src={post.coverImage}
                    alt={post.title}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                )}
              </div>

              <div className="flex flex-col flex-1">
                <div className="flex items-center justify-between mb-3">
                  {post.category && (
                    <span className="text-xs font-semibold text-premium-cyan uppercase tracking-wider">
                      {post.category}
                    </span>
                  )}
                  {post.readTime && <span className="text-xs text-slate-500">{post.readTime}</span>}
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-premium-violet transition-colors">
                  {post.title}
                </h3>
                <p className="text-sm text-slate-400 line-clamp-3 mb-4 flex-1">
                  {post.excerpt}
                </p>
                <div className="mt-auto pt-4 border-t border-white/5 flex items-center text-xs text-slate-500">
                  {post.date && <span>{new Date(post.date).toLocaleDateString()}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-24">
            <p className="text-slate-500 text-lg">No articles found.</p>
          </div>
        )}

      </section>

      <Newsletter />
    </section>
  )
}
